'use client';

import {
  type ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { Button } from '../shadcn/button';

/**
 * Bearbeiten am Telefon — EIN Knopf je Abschnitt statt eines Stifts je Feld
 * (Auftraggeber-Befund 07.10.2026, Bewerberprofil bei 390 px: „an jedem Feld
 * ein Stift — nicht schön").
 *
 * Unter 768 px tragen die Felder eines `Feldraster` mit `bearbeiten` keinen
 * Stift. Im Kopf der Karte steht stattdessen „Bearbeiten" (`quiet sm`); ein
 * Tippen schaltet den Abschnitt um, erst dann erscheinen die Stifte, „Fertig"
 * schaltet zurück. Ab 768 px ändert sich nichts — der Knopf ist dort nicht da
 * (`md:hidden`), der Stift steht am Wert.
 *
 * 🔴 Befund 454: das ist KEINE Geräte-Sperre. Am Telefon wird hier geändert,
 * nur einen Schritt später; kein Knopf ist ausgegraut, keiner sagt „geht
 * nicht". Wo am Telefon gar nicht geändert wird (`FeldrasterAmTelefonNurLesen`,
 * Haushalt), meldet sich das Raster nicht an — dort gibt es auch kein
 * „Bearbeiten".
 *
 * WER DEN KNOPF ZEIGT: die `Karte` trägt den Rahmen und setzt den Knopf in
 * ihren Kopf. Ein `Feldraster` ohne Karte darum trägt einen eigenen Rahmen und
 * setzt den Knopf über sich. Gezeigt wird er nur, solange sich darin ein
 * Raster mit bearbeitbaren Feldern ANGEMELDET hat — das geht nur nach dem
 * Einhängen, weil der Kopf vor dem Inhalt gerendert wird; daher der Effekt.
 *
 * Texte kommen vom Raster (`FeldrasterTexte.abschnittBearbeiten`/`fertig`);
 * das Bauteil trägt keine eigenen.
 */
export type TelefonKnopfTexte = { bearbeiten: string; fertig: string };

type TelefonBearbeitung = {
  an: boolean;
  umschalten: () => void;
  anmelden: (texte: TelefonKnopfTexte) => () => void;
  texte: TelefonKnopfTexte | null;
  anzahl: number;
};

const TelefonBearbeitungKontext = createContext<TelefonBearbeitung | null>(
  null,
);

export function useTelefonBearbeitung() {
  return useContext(TelefonBearbeitungKontext);
}

/**
 * Der Rahmen eines Abschnitts. Steht schon einer darüber, gilt der äussere —
 * `FeldAbschnitt` legt ihn um seine Karte, damit „Ergänzen" (vor der Karte)
 * den Abschnitt gleich in den Bearbeiten-Zustand schalten kann.
 */
export function TelefonBearbeitungRahmen({
  children,
}: {
  children: ReactNode;
}) {
  const aussen = useContext(TelefonBearbeitungKontext);

  if (aussen) return <>{children}</>;

  return <EigenerRahmen>{children}</EigenerRahmen>;
}

function EigenerRahmen({ children }: { children: ReactNode }) {
  const [zustand, setZustand] = useState<{
    an: boolean;
    anzahl: number;
    texte: TelefonKnopfTexte | null;
  }>({ an: false, anzahl: 0, texte: null });

  const umschalten = useCallback(
    () => setZustand((z) => ({ ...z, an: !z.an })),
    [],
  );

  const anmelden = useCallback((texte: TelefonKnopfTexte) => {
    setZustand((z) => ({ ...z, anzahl: z.anzahl + 1, texte }));

    return () => setZustand((z) => ({ ...z, anzahl: z.anzahl - 1 }));
  }, []);

  const wert = useMemo(
    () => ({ ...zustand, umschalten, anmelden }),
    [zustand, umschalten, anmelden],
  );

  return (
    <TelefonBearbeitungKontext.Provider value={wert}>
      {children}
    </TelefonBearbeitungKontext.Provider>
  );
}

/**
 * Ein Raster mit bearbeitbaren Feldern meldet sich beim Rahmen an; `null`
 * meldet nichts an (nur lesen, nur am Rechner, keine Felder mit `bearbeiten`).
 */
export function useTelefonAnmeldung(texte: TelefonKnopfTexte | null) {
  const rahmen = useContext(TelefonBearbeitungKontext);
  const anmelden = rahmen?.anmelden;
  const bearbeiten = texte?.bearbeiten;
  const fertig = texte?.fertig;

  useEffect(() => {
    if (!anmelden || bearbeiten === undefined || fertig === undefined) return;

    return anmelden({ bearbeiten, fertig });
  }, [anmelden, bearbeiten, fertig]);
}

/**
 * „Bearbeiten" / „Fertig" — nur unter 768 px, nur wenn sich ein Raster
 * angemeldet hat. `alleinstehend`: ohne Kartenkopf, rechtsbündig über dem
 * Inhalt.
 */
export function TelefonBearbeitenKnopf({
  alleinstehend = false,
}: {
  alleinstehend?: boolean;
}) {
  const rahmen = useContext(TelefonBearbeitungKontext);

  if (!rahmen || rahmen.anzahl < 1 || !rahmen.texte) return null;

  const knopf = (
    <Button
      variant="quiet"
      size="sm"
      onClick={rahmen.umschalten}
      data-test="abschnitt-bearbeiten-telefon"
      data-zustand={rahmen.an ? 'an' : 'aus'}
      className="md:hidden"
    >
      {rahmen.an ? rahmen.texte.fertig : rahmen.texte.bearbeiten}
    </Button>
  );

  if (!alleinstehend) return knopf;

  return <div className="mb-3 flex justify-end md:hidden">{knopf}</div>;
}
